import { type MiddlewareFunction, type Params } from 'react-router';

import { dangerouslyConvertPrivacyString } from '@trello/privacy';
import { isMeAlias } from '@trello/urls';

import { redirect } from '../migration';
import { usernameContext } from './context';

interface UsernameRedirectOptions {
  createRedirectUrl: (username: string) => string;
  getUsernameFromParams: (params: Params<string>) => string | undefined;
}

/**
 * Redirect to the actual username URL if the username parameter is:
 * 1. A "me" alias (me, my, you, your, username)
 * 2. The current user's memberId
 *
 * If the user is not logged in, redirect to the login page.
 *
 * We may consider splitting this into two middleware functions: one for the me alias and one for the memberId,
 * if the memberId logic detection will become more complex.
 */
export const createUsernameRedirect =
  ({
    createRedirectUrl,
    getUsernameFromParams,
  }: UsernameRedirectOptions): MiddlewareFunction =>
  async ({ context, params }): Promise<Response | void> => {
    const username = getUsernameFromParams(params);
    if (!username) {
      return; // No username dynamic segment in URL to check
    }

    const { memberId, username: currentUsername } =
      context.get(usernameContext) ?? {};

    // Check if the username is a me alias or matches the current user's memberId
    const shouldRedirect =
      isMeAlias(username) || (memberId && username === memberId);

    if (shouldRedirect) {
      const loginUrl = `/login?returnUrl=${encodeURIComponent(
        window.location.pathname + window.location.search,
      )}`;

      if (!currentUsername) {
        window.location.assign(loginUrl);
        // Return a 401 response to stop further processing of the route
        return new Response(null, {
          status: 401,
        });
      }

      const redirectUrl = createRedirectUrl(
        encodeURIComponent(dangerouslyConvertPrivacyString(currentUsername)),
      );

      return redirect(redirectUrl);
    }
  };
